import React, { ChangeEvent, forwardRef, ReactElement, Ref, useMemo, useState } from 'react'
import clsx from 'clsx'
import Tabs, { TabsProps } from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { useNavigate } from 'react-router-dom'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './Tabs.module.scss'

const TAB_INDICATOR_PROPS = { className: styles.TabsTwoIndicator }

type TabsTwoClassesProps = {
  // TabsProps classes
  flexContainer?: string
  scrollButtons?: string

  // Custom
  inputSearch?: string
  tab?: string
  tabs?: string
}

export type TabItem<ValueType = string> = {
  className?: string
  disabled?: boolean
  value: ValueType
  label: string
  to?: string
  hide?: boolean
}

export type TabsTwoProps<ValueType> = Pick<
  TabsProps,
  'indicatorColor' | 'scrollButtons' | 'textColor'
> & {
  borderless?: boolean
  classes?: TabsTwoClassesProps
  className?: string
  disableUppercase?: boolean
  items: TabItem<ValueType>[]
  onChange?: (event: React.ChangeEvent<{}>, value: ValueType) => void
  renderItem?: Function
  setRef?: React.MutableRefObject<HTMLDivElement | null>
  value?: ValueType
}

function TabsTwo<ValueType = string>(props: TabsTwoProps<ValueType>, ref: Ref<HTMLDivElement>) {
  const {
    borderless,
    indicatorColor,
    onChange,
    classes,
    className,
    disableUppercase,
    items,
    renderItem,
    scrollButtons,
    textColor,
    value
  } = props

  const navigate = useNavigate()
  const [valueTabs, setValueTabs] = useState(items?.[0]?.value)
  const isControlledComponent = typeof value !== 'undefined'
  const valueAdjusted = isControlledComponent ? value : valueTabs

  const TabsContentComponent = useMemo(() => {
    return items.map((item, index) => {
      if (renderItem) return renderItem(item, index)

      return (
        <Tab
          key={`${item.value}`}
          className={clsx(
            styles.TabsTwoTab,
            item.hide && styles.TabsTwoTabHidden,
            disableUppercase ? stylesGlobal.TextNormalcase : undefined,
            valueAdjusted === item.value && stylesGlobal.FontBold,
            classes?.tab,
            item?.className
          )}
          value={item.value}
          disabled={item.disabled}
          onClick={
            item.to
              ? () => {
                  if (item.to) navigate(item.to)
                }
              : undefined
          }
          label={item.label}
        />
      )
    })
  }, [items, renderItem, disableUppercase, classes?.tab, valueAdjusted, navigate])

  return (
    <div
      className={clsx(
        styles.TabsTwoContainer,
        !borderless && styles.TabsTwoContainerBordered,
        className
      )}
      ref={ref}
    >
      <Tabs
        classes={
          classes?.tabs || classes?.flexContainer || classes?.scrollButtons
            ? {
                root: classes?.tabs,
                flexContainer: classes?.flexContainer,
                scrollButtons: classes?.scrollButtons
              }
            : undefined
        }
        variant="scrollable"
        className={styles.TabsTwo}
        indicatorColor={indicatorColor}
        onChange={(event, value) => {
          if (!isControlledComponent) setValueTabs(value)
          onChange?.(event, value)
        }}
        scrollButtons={scrollButtons}
        TabIndicatorProps={TAB_INDICATOR_PROPS}
        textColor={textColor}
        value={valueAdjusted}
      >
        {TabsContentComponent}
      </Tabs>
    </div>
  )
}

export default forwardRef(TabsTwo) as <ValueType = string>(
  props: TabsTwoProps<ValueType> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement
